import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  body: z.string().min(1, "Body is required"),
  type: z.string().min(1, "Type is required"),
  target_user_id: z.coerce.number().optional(),
  sender_id: z.coerce.number().optional(),
});

interface NotificationEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: any;
  setForm: (updater: (prev: any) => any) => void;
  onSave: (data: any) => Promise<void> | void;
}

export default function NotificationEditDialog({ 
  open, 
  onOpenChange, 
  form, 
  setForm, 
  onSave 
}: NotificationEditDialogProps) {
  const [loading, setLoading] = React.useState(false);
  
  const rhf = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: form,
    values: form,
  });

  React.useEffect(() => {
    rhf.reset(form);
  }, [form, open]);

  async function handleSubmit(data: z.infer<typeof formSchema>) {
    setLoading(true);
    const merged = { ...form, ...data };
    setForm(() => merged);
    await onSave(merged);
    setLoading(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Notification</DialogTitle>
          <DialogDescription>Update notification details below and click Save Changes.</DialogDescription>
        </DialogHeader>
        <Form {...rhf}>
          <form className="space-y-4" onSubmit={rhf.handleSubmit(handleSubmit)}>
            <FormField
              control={rhf.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={rhf.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Body</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={rhf.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Notification Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="info">Info</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="success">Success</SelectItem>
                        <SelectItem value="error">Error</SelectItem>
                        <SelectItem value="promotional">Promotional</SelectItem>
                        <SelectItem value="reminder">Reminder</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={rhf.control}
                name="target_user_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target User ID</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      0 for all users, or specify a user ID
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={rhf.control}
                name="sender_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sender ID</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      ID of the sender
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
